// Navigation - appears on every page.
// Desktop: logo, a floating capsule with a sliding highlight, and theme/profile on the right.
// Mobile: a slim top bar and an app-style dock at the bottom (icons above labels), with "Mer" for the rest.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signInWithPopup, signOut, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { app } from './firebase-config';
import {
  FiHome, FiCalendar, FiBookOpen, FiAward, FiImage, FiUsers, FiGlobe, FiTool, FiSun, FiMoon, FiMonitor,
  FiGrid, FiX, FiLogIn, FiLogOut, FiUser,
} from 'react-icons/fi';
import logo from './assets/logo.png';
import { useRoles } from './adminData';
import { useTheme } from './theme';
import { SmartImage } from './imageUtils';

const auth = getAuth(app);
const db = getFirestore(app);

// Keeps emails/{uid} up to date, so blåtur organizers can e-mail the invitation list.
// Stored outside the public profile: only the owner and blåtur admins can read it.
const syncEmail = async (currentUser) => {
  if (!currentUser?.email) return;
  const key = `${currentUser.uid}:${currentUser.email}`;
  if (localStorage.getItem('emailSynced') === key) return;
  try {
    await setDoc(doc(db, 'emails', currentUser.uid), { email: currentUser.email });
    localStorage.setItem('emailSynced', key);
  } catch (error) {
    console.error('Error saving e-mail: ', error);
  }
};

const LINKS = [
  { to: '/', label: 'Hjem', icon: FiHome, dock: true },
  { to: '/forside', label: 'Eventer', icon: FiCalendar, dock: true },
  { to: '/blatur', label: 'Blåtur', icon: FiGlobe, dock: true },
  { to: '/scoreboard', label: 'Poeng', icon: FiAward, dock: true },
  { to: '/bilder', label: 'Bilder', icon: FiImage },
  { to: '/profiler', label: 'Medlemmer', icon: FiUsers },
  { to: '/regler', label: 'Regler', icon: FiBookOpen },
];

const isActive = (pathname, to) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

function MyNavbar() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isBlaturAdmin } = useRoles(user);
  const { preference, cycle } = useTheme();
  const { pathname } = useLocation();

  const capsuleRef = useRef(null);
  const [indicator, setIndicator] = useState(null);

  const themeLabel = { system: 'Tema: følger systemet', light: 'Tema: lyst', dark: 'Tema: mørkt' }[preference];
  const ThemeIcon = { system: FiMonitor, light: FiSun, dark: FiMoon }[preference];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      syncEmail(currentUser);
      if (currentUser) {
        const snap = await getDoc(doc(db, 'users', currentUser.uid)).catch(() => null);
        setProfile(snap?.exists() ? snap.data() : null);
      } else {
        setProfile(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => setSheetOpen(false), [pathname]);

  // Sliding highlight behind the active link in the capsule
  useLayoutEffect(() => {
    const place = () => {
      const active = capsuleRef.current?.querySelector('[data-active="true"]');
      setIndicator(active ? { left: active.offsetLeft, width: active.offsetWidth } : null);
    };
    place();
    window.addEventListener('resize', place);
    document.fonts?.ready.then(place);
    return () => window.removeEventListener('resize', place);
  }, [pathname, isBlaturAdmin]);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const userDocRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        const userName = prompt('Skriv inn ditt fornavn:');
        if (userName) {
          await setDoc(userDocRef, { name: userName, points: 0, attended_events: [] });
        }
      }

      const userData = (await getDoc(userDocRef)).data();
      localStorage.setItem('userName', userData.name);
      localStorage.setItem('userPoints', userData.points);
      setProfile(userData);
    } catch (error) {
      console.error('Error with Google sign-in:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('userName');
      localStorage.removeItem('userPoints');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const links = isBlaturAdmin ? [...LINKS, { to: '/admin', label: 'Admin', icon: FiTool }] : LINKS;
  const initials = (profile?.name || user?.displayName || '?').trim().slice(0, 1).toUpperCase();
  const moreActive = links.filter(l => !l.dock).some(l => isActive(pathname, l.to)) || pathname === '/minprofil';

  const Avatar = () => (
    <span className="nav-avatar">
      {profile?.profileImageUrl ? <SmartImage src={profile.profileImageUrl} size={200} loading="eager" /> : initials}
    </span>
  );

  return (
    <>
      <header className="nav-bar">
        <Link to="/" className="nav-brand" aria-label="Kulinarisk Laug, til forsiden">
          <img src={logo} alt="" />
          <span>Kulinarisk&nbsp;Laug</span>
        </Link>

        <nav className="nav-capsule" ref={capsuleRef} aria-label="Hovedmeny">
          {indicator && <span className="nav-indicator" style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }} />}
          {links.map(l => (
            <Link key={l.to} to={l.to} className="nav-capsule-link" data-active={isActive(pathname, l.to)}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="nav-actions">
          <button type="button" className="nav-icon-btn" onClick={cycle} title={themeLabel} aria-label={themeLabel}>
            <ThemeIcon />
          </button>
          {user ? (
            <>
              <Link to="/minprofil" className="nav-profile" title="Min deltakelse">
                <Avatar />
                <span className="nav-profile-name">{profile?.name?.trim() || 'Min side'}</span>
              </Link>
              <button type="button" className="nav-icon-btn nav-logout" onClick={handleLogout} title="Logg ut" aria-label="Logg ut">
                <FiLogOut />
              </button>
            </>
          ) : (
            <button type="button" className="button-17 nav-login" onClick={handleLogin}>
              <FiLogIn /> Logg inn
            </button>
          )}
        </div>
      </header>

      {/* Mobile dock */}
      <nav className="nav-dock" aria-label="Hovedmeny">
        {links.filter(l => l.dock).map(l => {
          const Icon = l.icon;
          return (
            <Link key={l.to} to={l.to} className="nav-dock-item" data-active={isActive(pathname, l.to)}>
              <Icon aria-hidden="true" />
              <span>{l.label}</span>
            </Link>
          );
        })}
        <button type="button" className="nav-dock-item" data-active={moreActive || sheetOpen} onClick={() => setSheetOpen(o => !o)}>
          {sheetOpen ? <FiX aria-hidden="true" /> : <FiGrid aria-hidden="true" />}
          <span>Mer</span>
        </button>
      </nav>

      {sheetOpen && (
        <div className="nav-sheet-backdrop" onClick={() => setSheetOpen(false)}>
          <div className="nav-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="nav-sheet-grid">
              {links.filter(l => !l.dock).map(l => {
                const Icon = l.icon;
                return (
                  <Link key={l.to} to={l.to} className="nav-sheet-item" data-active={isActive(pathname, l.to)}>
                    <span className="nav-sheet-icon"><Icon /></span>
                    <span>{l.label}</span>
                  </Link>
                );
              })}
              {user && (
                <Link to="/minprofil" className="nav-sheet-item" data-active={pathname === '/minprofil'}>
                  <span className="nav-sheet-icon"><FiUser /></span>
                  <span>Min side</span>
                </Link>
              )}
            </div>
            <div className="nav-sheet-footer">
              <button type="button" className="button-17 muted" onClick={cycle}><ThemeIcon /> {themeLabel}</button>
              {user
                ? <button type="button" className="button-17 muted" onClick={handleLogout}><FiLogOut /> Logg ut</button>
                : <button type="button" className="button-17" onClick={handleLogin}><FiLogIn /> Logg inn</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MyNavbar;
